/**
 * A história do LFRCORD: por que ele existe.
 *
 * Um componente só, usado nos dois lugares — a aba "Sobre" da tela de entrada
 * (quem ainda não tem conta) e o botão "História" dentro do app. O texto não
 * pode viver em dois arquivos: mudaria num e ficaria velho no outro.
 */
import type { CSSProperties, ReactElement } from "react";
import { C, FONT_MONO } from "@/theme";

const ROTULO: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
  textTransform: "uppercase",
};

const TITULO: CSSProperties = {
  fontSize: 19,
  fontWeight: 700,
  color: C.textStrong,
  margin: "0 0 8px",
  lineHeight: 1.25,
};

const PARAGRAFO: CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.65,
  color: C.textSoft,
  margin: "0 0 12px",
};

const DESTAQUE: CSSProperties = {
  ...PARAGRAFO,
  color: C.textStrong,
  fontWeight: 600,
};

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }): ReactElement {
  return (
    <section style={{ marginBottom: 26 }}>
      <h3 style={TITULO}>{titulo}</h3>
      {children}
    </section>
  );
}

/**
 * `compacto` encolhe o respiro para caber na coluna estreita da tela de
 * entrada, onde o cartão divide espaço com o formulário.
 */
export function Historia({ compacto = false }: { compacto?: boolean }): ReactElement {
  return (
    <div style={{ maxWidth: 620, padding: compacto ? "4px 2px" : "2px 2px 18px" }}>
      <div style={{ ...ROTULO, marginBottom: 10 }}>Por que fomos criados</div>

      <Bloco titulo="A tela apagou">
        <p style={PARAGRAFO}>
          A LFR CREW sempre viveu em chamada. Jogo, trabalho, madrugada resolvendo bug — tudo
          acontecia com a tela de alguém compartilhada e a câmera aberta. Era assim que a gente se
          via, ensinava um ao outro e existia como grupo.
        </p>
        <p style={PARAGRAFO}>
          Até que, no Brasil, transmitir a tela e ligar a câmera deixaram de ser possíveis no
          aplicativo que a gente usava. A decisão veio de fora — não foi escolha nossa, nem do
          grupo, nem de ninguém que estivesse na chamada. Um dia funcionava. No outro, o botão
          simplesmente não estava mais lá.
        </p>
        <p style={PARAGRAFO}>
          O que se perdeu não foi um recurso. Foi o jeito da crew conversar.
        </p>
      </Bloco>

      <Bloco titulo="A saída que não veio">
        <p style={PARAGRAFO}>
          A gente tentou de tudo. Espalhou o pessoal por três ou quatro serviços diferentes,
          cada um com metade do que precisava. Testou alternativa que travava com quatro pessoas
          na sala. Aceitou vídeo borrado, som cortando, e aquela sensação de estar sempre pedindo
          desculpa pela qualidade.
        </p>
        <p style={PARAGRAFO}>
          Nada devolvia o que existia antes: entrar numa sala, apertar um botão e a crew inteira
          ver a sua tela nítida, em tempo real, sem pensar no assunto.
        </p>
      </Bloco>

      <Bloco titulo="Então a gente construiu">
        <p style={DESTAQUE}>
          O LFRCORD nasceu de uma recusa simples: se ninguém ia devolver, a gente fazia.
        </p>
        <p style={PARAGRAFO}>
          É um aplicativo da LFR CREW, feito para a LFR CREW. Canais de texto, mensagens diretas,
          salas de voz — e, no centro de tudo, a transmissão de tela que motivou o projeto:{" "}
          <strong style={{ color: C.textStrong }}>1080p a 60 quadros por segundo</strong>, com
          escada até 4K, câmera junto, e o vídeo indo direto de quem transmite para quem assiste,
          sem passar por servidor de ninguém no meio.
        </p>
        <p style={PARAGRAFO}>
          Essa última parte não é detalhe técnico: é a razão de a imagem chegar limpa. Numa malha
          ponto-a-ponto o vídeo não é reencodado por um intermediário, e o que sai da sua placa é
          o que aparece na tela do outro.
        </p>
      </Bloco>

      <Bloco titulo="O que isso significa">
        <p style={PARAGRAFO}>
          Não somos uma empresa e não estamos disputando mercado com ninguém. O LFRCORD roda em
          servidor próprio, com o código na mão de quem usa, e existe enquanto a crew precisar
          dele.
        </p>
        <p style={PARAGRAFO}>
          A lição que ficou é a que mantém o projeto de pé:{" "}
          <strong style={{ color: C.textStrong }}>
            depender de um lugar que você não controla é aceitar que um dia ele mude sem te
            perguntar
          </strong>
          . Aqui, o dia em que algo mudar vai ser porque a gente decidiu mudar.
        </p>
      </Bloco>

      <div
        style={{
          ...ROTULO,
          paddingTop: 14,
          borderTop: `1px solid ${C.lineSoft}`,
          color: C.textDim,
        }}
      >
        LFRCORD · um aplicativo da LFR CREW
      </div>
    </div>
  );
}
